import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { Observable } from 'rxjs';
import { mergeMap, finalize } from 'rxjs/operators';

import { AddressService } from '../../services/address/address.service';
import { Location } from '../../services/address/location.model';
import { AuthService } from '../../services/auth/auth.service';
import { Severity } from '../../services/flash-messages/flash-message.model';
import { FlashMessagesService } from '../../services/flash-messages/flash-messages.service';
import { SearchService } from '../../services/search/search.service';
import { Site } from '../../services/sites/site.model';
import { SitesService } from '../../services/sites/sites.service';
import { ReturnUrlService } from '../../services/return-url/return-url.service';
import { WindowService } from '../../services/window/window.service';

const placeHolderText: any = {
    "default": "3 Grant St, Cleveland QLD 4163, Australia",
    "yarra ranges": "2442-2444 Warburton Hwy, Yarra Junction VIC 3797, Australia"
};

@Component({
    selector: 'app-address-search',
    templateUrl: './address-search.component.html',
    styleUrls: ['./address-search.component.css']
})
export class AddressSearchComponent implements OnDestroy {
    address: string;
    savedAddress: string;
    dataSource: Observable<any>;

    messageIds = {
        httpError: <string>null
    };
    typeaheadLoading: boolean;
    typeaheadNoResults: boolean;

    private lastSelection: string;
    private multipleHighAccuracyResultsMessageId: string;
    private noHighAccuracyResultsMessageId: string;

    get canSearch(): boolean {
        return !this.selectSiteEnabled || this.searchService.site !== null;
    }

    get selectSiteEnabled(): boolean {
        return this.authService.userInfo.isInRole('jjr');
    }

    constructor(activatedRoute: ActivatedRoute,
        public authService: AuthService,
        private flashMessagesService: FlashMessagesService,
        private returnUrlService: ReturnUrlService,
        public searchService: SearchService,
        sitesService: SitesService,
        private mappingService: AddressService,
        private router: Router,
        private windowService: WindowService) {

        this.dataSource = new Observable(observer => {
            // Runs on every search.
            if (this.address && this.searchService.site) {
                this.typeaheadNoResults = false;
                this.typeaheadLoading = true;
                observer.next(this.address);
            }
        }).pipe(
            mergeMap((text: string) =>
                this.mappingService.findSuggestions(text, this.searchService.site.siteNumber).pipe(
                    finalize(() => {
                        this.typeaheadLoading = false;
                    })
                )
            )
        );

        activatedRoute.params.subscribe(params => {
            const siteParam = params['site'];
            if (siteParam) {
                sitesService.get(siteParam).subscribe(result => {
                    this.searchService.site = result;
                    this.searchQueryParamsAddress();
                }, error => {
                    this.messageIds.httpError = flashMessagesService.addMessageFor(error);
                });
            } else {
                this.searchQueryParamsAddress();
            }
        });

        activatedRoute.queryParams.subscribe(params => {
            const add = params['address'];
            if (add && add !== this.address) {
                this.address = add;
                this.searchQueryParamsAddress();
            }
        });

        this.returnUrlService.save();
    }

    getPlaceHolderText(): string {
        const placeHolderTextName = (this.authService.userInfo.isInRole('council') ? this.authService.userInfo.councilName : 'default').toLowerCase();
        return `Address e.g. ${placeHolderText[placeHolderTextName]}`;
    }

    ngOnDestroy(): void {
        this.searchService.clearAll();
    }

    onTypeaheadLoading(): void {
        // Clear the 'no typeahead results' (and all warnings) as soon as the user changes the input.
        this.typeaheadNoResults = false;
        this.clearWarnings();
        this.searchService.clearWarnings();
    }

    onTypeaheadNoResults(noResults: boolean): void {
        this.typeaheadNoResults = noResults;
    }

    onTypeaheadValueSelected(match: TypeaheadMatch): void {
        this.setAddressLatitudeLongitude(match.item.address, match.item.latitude, match.item.longitude);
    }

    clearAddress(): void {
        this.address = null;
    }

    search(): void {
        this.windowService.localStorage.setItem('addressSearchValue', this.address);

        const commands = ['address-search'];
        // Include the 'site' in the URL if it's defined.
        if (this.searchService.site) {
            commands.push(this.searchService.site.siteNumber);
        }
        const extras: NavigationExtras = { queryParams: {} };
        // Include the 'address' in the URL if it's defined.
        if (this.address) {
            extras.queryParams.address = this.address;
        }

       this.savedAddress = this.windowService.localStorage.getItem('addressSearchValue');

        // noinspection JSIgnoredPromiseFromCall
        this.router.navigate(commands, extras).then((success: boolean) => {
            this.searchService.search(this.savedAddress, 0, 0);
            this.address = this.searchService.address;
            this.returnUrlService.save();
        });
    }

    selectSite(site: Site): void {
        this.searchService.selectSite(site);
        this.address = null;
    }

    validateAddress(): void {
        // Only allow latitude/longitude from typeahead-selected suggestions.
        if (!this.address || this.address !== this.lastSelection) {
            this.searchService.latitude = null;
            this.searchService.longitude = null;
            this.searchService.clearResults();
        }
    }

    private clearWarnings(): void {
        this.flashMessagesService.clear(this.multipleHighAccuracyResultsMessageId);
        this.flashMessagesService.clear(this.noHighAccuracyResultsMessageId);
        this.searchService.clearWarnings();
    }

    private searchFromLocations(locations: Location[]): void {
        const highAccuracyLocations = locations.filter(location => location.confidence === 'High');
        if (highAccuracyLocations.length === 0) {
            this.typeaheadNoResults = true;
            this.noHighAccuracyResultsMessageId = this.flashMessagesService.addMessage(
                Severity.Warning, `Unable to accurately determine the GPS location of: ${this.address}.`);
        } else {
            if (highAccuracyLocations.length > 1) {
                this.multipleHighAccuracyResultsMessageId = this.flashMessagesService.addMessage(
                    Severity.Warning, `Multiple locations found for: ${this.address}. Selecting the first one.`);
            }
            const location = highAccuracyLocations[0];
            this.setAddressLatitudeLongitude(location.address, location.latitude, location.longitude);
            this.searchService.search(this.savedAddress, 0, 0);
        }
    }

    private searchQueryParamsAddress(): void {
        if (this.address &&
            this.lastSelection &&
            this.searchService.latitude &&
            this.searchService.longitude &&
            this.address === this.lastSelection) {
            // If we already have all the data we need to search GPS_LOG.
            this.searchService.search(this.savedAddress, 0, 0);
        } else if (this.address && this.searchService.site) {
            this.typeaheadLoading = true;
            // If we have an address and site (e.g. from the query params) but haven't worked out the lat/long yet.
            this.mappingService.findSuggestions(this.address, this.searchService.site.siteNumber).subscribe(l => {
                this.typeaheadLoading = false;
                this.searchFromLocations(l);
            });
        }
    }

    private setAddressLatitudeLongitude(address: string, latitude: number, longitude: number): void {
        this.address = address;
        this.lastSelection = address;
        this.searchService.latitude = latitude;
        this.searchService.longitude = longitude;
    }
}
